// Pure logic behind the sidebar group state (stores/navGroups.js). Nothing here touches Vue,
// the router or localStorage, so every rule can be unit-tested with a fake tree.

export const NAV_GROUPS_STORAGE_VERSION = 1;

// Titles repeat across marketplaces ("Orders" under Amazon and under eBay), so a group is
// identified by the titles of all its ancestors joined with this separator. No group title
// may contain it (asserted against the real tree in navGroups.spec.js).
export const GROUP_KEY_SEPARATOR = '/';

// provide/inject channel: each VerticalNavGroup injects its parent's key and provides its own.
export const injectionKeyNavGroupKey = Symbol('navGroupKey');

export const buildGroupKey = (parentKey, title) =>
  parentKey ? `${parentKey}${GROUP_KEY_SEPARATOR}${title}` : title;

const isGroup = (item) => !!item && Array.isArray(item.children);

const parentKeyOf = (key) => {
  const index = key.lastIndexOf(GROUP_KEY_SEPARATOR);
  return index === -1 ? '' : key.slice(0, index);
};

const isSelfOrDescendant = (candidate, key) =>
  candidate === key || candidate.startsWith(`${key}${GROUP_KEY_SEPARATOR}`);

export const collectGroupKeys = (tree, parentKey = '') => {
  const keys = [];
  (tree || []).forEach((item) => {
    if (!isGroup(item)) return;
    const key = buildGroupKey(parentKey, item.title);
    keys.push(key, ...collectGroupKeys(item.children, key));
  });
  return keys;
};

// Every group key that has a descendant link for which `isLinkActive` is true. A Set because
// one route can be linked from two groups; all such paths are part of the current path.
export const computeCurrentPathKeys = (tree, isLinkActive, parentKey = '') => {
  const result = new Set();
  (tree || []).forEach((item) => {
    if (!isGroup(item)) return;
    const key = buildGroupKey(parentKey, item.title);
    const inner = computeCurrentPathKeys(item.children, isLinkActive, key);
    const hasActiveLink = item.children.some((child) => !isGroup(child) && isLinkActive(child));
    if (hasActiveLink || inner.size) result.add(key);
    inner.forEach((innerKey) => result.add(innerKey));
  });
  return result;
};

export const pruneOpenKeys = (open, validKeys) => {
  const valid = new Set(validKeys);
  return [...open].filter((key) => valid.has(key));
};

// Returns `{ open, auto }` or null when nothing usable is stored (absent, garbage JSON,
// unknown version, wrong shape).
export const parseStoredState = (raw) => {
  if (typeof raw !== 'string' || !raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || parsed.v !== NAV_GROUPS_STORAGE_VERSION)
      return null;
    if (!Array.isArray(parsed.open)) return null;
    return {
      open: parsed.open.filter((key) => typeof key === 'string'),
      auto: parsed.auto === true,
    };
  } catch {
    return null;
  }
};

export const serializeState = ({ open, auto }) =>
  JSON.stringify({ v: NAV_GROUPS_STORAGE_VERSION, open: [...open], auto: !!auto });

const union = (a, b) => [...new Set([...a, ...b])];

// First route resolution. `stored` is the pruned stored state, or null when none existed.
export const resolveInitialOpen = ({ stored, currentPathKeys }) => {
  const path = [...currentPathKeys];
  if (!stored) return path;
  if (!path.length) return [...stored.open];
  return stored.auto ? path : union(stored.open, path);
};

// Route change. A page that is not in the menu (empty path) changes nothing in either mode.
export const resolveRouteChangeOpen = ({ open, auto, currentPathKeys }) => {
  const path = [...currentPathKeys];
  if (!path.length) return [...open];
  return auto ? path : union(open, path);
};

export const resolveToggleOpen = ({ open, key, auto, currentPathKeys, allKeys }) => {
  const current = new Set(open);
  if (current.has(key)) {
    return [...open].filter((candidate) => !isSelfOrDescendant(candidate, key));
  }
  if (!auto) return [...open, key];

  const parent = parentKeyOf(key);
  const closing = allKeys.filter(
    (candidate) =>
      candidate !== key && parentKeyOf(candidate) === parent && !currentPathKeys.has(candidate),
  );
  const kept = [...open].filter(
    (candidate) => !closing.some((sibling) => isSelfOrDescendant(candidate, sibling)),
  );
  return [...kept, key];
};

export const resolveCollapseAll = ({ currentPathKeys }) => [...currentPathKeys];

export const resolveExpandAll = ({ allKeys }) => [...allKeys];
